import { NetworkName } from '@coinweb/wallet-lib/enums';

import { ERC20Currency, EvmCurrency, SolanaCurrency, SplCurrency, Trc20Currency, TronCurrency } from '../types';

import { Currency, ERC20_TOKENS, EVM_NATIVE_TOKENS, SOLANA_NATIVE_TOKENS, SPL_TOKENS } from './tokens';

type CommonNames = 'NETWORK_NAME' | `L2_CONTRACT_ADDRESS_${'BASE' | 'MAKER'}` | 'L2_COLLATERAL_PERCENTAGE_MAKER';

type EvmNames =
  | `L1_CONTRACT_ADDRESS_${'BASE' | 'MAKER'}`
  | `L1_CONTRACT_ABI_${'BASE' | 'MAKER'}`
  | `L1_CALL_METHOD_NAME_${'BASE' | 'MAKER'}`;

type TronNames = `L1_CONTRACT_ADDRESS_${'BASE' | 'MAKER'}`;

type SolanaNames = `L1_CONTRACT_ADDRESS_${'BASE' | 'MAKER'}`;

type ERC20Names = 'L1_TOKEN_ADDRESS';

type HexString = `0x${string}`;

type ParameterPossibleTypes = 'string' | 'number' | 'NetworkName' | 'HexString';

const commonParamsMap = {
  NETWORK_NAME: 'NetworkName',

  //C1
  L2_CONTRACT_ADDRESS_BASE: 'HexString',

  //C2
  L2_CONTRACT_ADDRESS_MAKER: 'HexString',
  L2_COLLATERAL_PERCENTAGE_MAKER: 'number',
} as const satisfies Record<CommonNames, ParameterPossibleTypes>;

const evmParamsMap = {
  //C1
  L1_CONTRACT_ADDRESS_BASE: 'HexString',
  L1_CONTRACT_ABI_BASE: 'string',
  L1_CALL_METHOD_NAME_BASE: 'string',

  //C2
  L1_CONTRACT_ADDRESS_MAKER: 'HexString',
  L1_CONTRACT_ABI_MAKER: 'string',
  L1_CALL_METHOD_NAME_MAKER: 'string',
} as const satisfies Record<EvmNames, ParameterPossibleTypes>;

const tronParamsMap = {
  //C1
  L1_CONTRACT_ADDRESS_BASE: 'string',

  //C2
  L1_CONTRACT_ADDRESS_MAKER: 'string',
} as const satisfies Record<TronNames, ParameterPossibleTypes>;

const ERC20ParamsMap = { L1_TOKEN_ADDRESS: 'HexString' } as const satisfies Record<ERC20Names, 'HexString'>;

const TRC20ParamsMap = { L1_TOKEN_ADDRESS: 'string' } as const satisfies Record<ERC20Names, 'string'>;

const solanaParamsMap = {
  L1_CONTRACT_ADDRESS_BASE: 'string',
  L1_CONTRACT_ADDRESS_MAKER: 'string',
} as const satisfies Record<SolanaNames, ParameterPossibleTypes>;

const SPLParamsMap = { L1_TOKEN_ADDRESS: 'string' } as const satisfies Record<ERC20Names, 'string'>;

type CommonParamsMap = typeof commonParamsMap;
type CommonParamName = keyof CommonParamsMap;

type EvmParamsMap = CommonParamsMap & typeof evmParamsMap;
type EvmParamName = keyof EvmParamsMap;

type ERC20ParamsMap = CommonParamsMap & EvmParamsMap & typeof ERC20ParamsMap;
type ERC20ParamName = keyof ERC20ParamsMap;

type TronParamsMap = CommonParamsMap & typeof tronParamsMap;
type TronParamName = keyof TronParamsMap;

type Trc20ParamsMap = CommonParamsMap & typeof tronParamsMap & typeof TRC20ParamsMap;
type Trc20ParamName = keyof Trc20ParamsMap;

type SolanaParamsMap = CommonParamsMap & typeof solanaParamsMap;
type SolanaParamName = keyof SolanaParamsMap;

type SPLParamsMap = CommonParamsMap & typeof solanaParamsMap & typeof SPLParamsMap;
type SPLParamName = keyof SPLParamsMap;

type ParameterType<PossibleType extends ParameterPossibleTypes> = PossibleType extends 'string'
  ? string
  : PossibleType extends 'HexString'
    ? HexString
    : PossibleType extends 'number'
      ? number
      : NetworkName;

export type ContractParams = {
  [C in Currency]: C extends EvmCurrency
    ? {
        [N in EvmParamName]: ParameterType<ERC20ParamsMap[N]>;
      }
    : C extends ERC20Currency
      ? {
          [N in ERC20ParamName]: ParameterType<ERC20ParamsMap[N]>;
        }
      : C extends TronCurrency
        ? {
            [N in TronParamName]: ParameterType<TronParamsMap[N]>;
          }
        : C extends Trc20Currency
          ? {
              [N in Trc20ParamName]: ParameterType<Trc20ParamsMap[N]>;
            }
          : C extends SolanaCurrency
            ? {
                [N in SolanaParamName]: ParameterType<SolanaParamsMap[N]>;
              }
            : C extends SplCurrency
              ? {
                  [N in SPLParamName]: ParameterType<SPLParamsMap[N]>;
                }
              : {
                  [N in CommonParamName]: ParameterType<CommonParamsMap[N]>;
                };
};

const extractParam = (currency: Currency, name: string | symbol) => {
  const paramsMap = (() => {
    switch (true) {
      case (ERC20_TOKENS as Currency[]).includes(currency):
        return { ...commonParamsMap, ...evmParamsMap, ...ERC20ParamsMap };
      case (EVM_NATIVE_TOKENS as Currency[]).includes(currency):
        return { ...commonParamsMap, ...evmParamsMap };
      case currency === Currency.TRX:
        return { ...commonParamsMap, ...tronParamsMap };
      case currency === Currency.USDT_TRX:
        return { ...commonParamsMap, ...tronParamsMap, ...TRC20ParamsMap };
      case (SOLANA_NATIVE_TOKENS as Currency[]).includes(currency):
        return { ...commonParamsMap, ...solanaParamsMap };
      case (SPL_TOKENS as Currency[]).includes(currency):
        return { ...commonParamsMap, ...solanaParamsMap, ...SPLParamsMap };

      default:
        return commonParamsMap;
    }
  })();

  const paramName = name as keyof typeof paramsMap;

  const param = import.meta.env[`VITE_${name as string}_${currency}`];

  if (paramsMap[paramName] === 'number' && !isNaN(Number(param))) {
    return Number(param);
  }

  if (paramsMap[paramName] === 'NetworkName' && Object.values(NetworkName).includes(param)) {
    return param as NetworkName;
  }

  if (paramsMap[paramName] === 'HexString' && typeof param === 'string' && param.slice(0, 2).toLowerCase() === '0x') {
    return param;
  }

  if (typeof param === paramsMap[paramName]) {
    return param as ParameterType<(typeof paramsMap)[typeof paramName]>;
  }
};

const checkIsCurrency = (value: unknown): value is Currency => (Object.values(Currency) as unknown[]).includes(value);

export const CONTRACT_PARAMS = new Proxy(
  {},
  {
    get(currencyParams: Record<string, unknown>, currency) {
      if (!checkIsCurrency(currency)) {
        throw new Error(`Unknown token ${String(currency)}`);
      }

      if (currency in currencyParams) {
        return currencyParams[currency as keyof typeof currencyParams];
      }

      if ((Object.values(Currency) as (string | symbol)[]).includes(currency)) {
        currencyParams[currency] = new Proxy(
          {},
          {
            get(params: Record<string | symbol, unknown>, paramName) {
              if (paramName in params) {
                return params[paramName as keyof typeof params];
              }

              params[paramName] = extractParam(currency, paramName);
              return params[paramName];
            },
          }
        );

        return currencyParams[currency];
      }
    },
  }
) as ContractParams;
